/* eslint-disable import/no-cycle */
import React from 'react'

import { Formik, Form } from 'formik'
import { withTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { v4 as uuid } from 'uuid'
import * as Yup from 'yup'

import { getFlightSchedule } from 'actions/flightSchedule'
import { setFlightStatusSearch } from 'actions/flightStatusSearch'
import {
  clearSuggestions,
  fetchSuggestions,
  fetchSuggestionsByIataCodes
} from 'actions/suggestions'
import Autocomplete from 'components/autocomplete'
import DatePickerFullResponsive from 'components/datepicker/date-picker-full-responsive'
import { ReCaptcha } from 'components/recaptchaV2'
import SwapFieldsButton from 'components/swap-fields-button'
import { searchBox as e2eClasses } from 'constants/e2eClasses'
import FLIGHT_STATUS from 'constants/flightStatus'
import FORMATS from 'constants/formats'
import MODAL_NAMES from 'constants/modalNames'
import { getDateWithFormat, getDayJsObjectFromLocal, isBeforeToday } from 'helpers/dates'
import { buildURLEncoded, getUrlParams } from 'helpers/url'
import { generateFlightStatusRoute } from 'helpers/url/localization'
import { handleValueChange, isEmpty, isMobile, isTablet } from 'helpers/utils'
import { mapMediaType } from 'selectors/mediaType'
import { getSuggestionsFromTo, getSuggestionsTo, getSuggestionsFrom } from 'selectors/suggestions'

import {
  Label,
  AirportContainer,
  Button,
  ButtonContainer,
  FormWrapper,
  SwapFieldsWrapper,
  InputWrapper,
  Separator
} from '../styled'

class FlightRoute extends React.Component {
  constructor(props) {
    super(props)

    const { departureDate, origin, destination } = this.getUrlParameters()

    this.state = {
      values: {
        departureDate:
          isBeforeToday(departureDate) || !departureDate
            ? getDayJsObjectFromLocal().toDate()
            : getDayJsObjectFromLocal(departureDate).toDate(),
        fromSelected: origin ? { display: origin || '', cityCode: origin } : undefined,
        toSelected: destination ? { display: destination || '', cityCode: destination } : undefined
      }
    }
    this.recaptchaRef = React.createRef()
  }

  getUrlParameters = () => getUrlParams(this.props.location.search.split('?').pop())

  componentDidMount = () => {
    this.origenInputId = uuid()
    this.destinationInputId = uuid()
    this.dateInputId = uuid()

    const urlParams = this.getUrlParameters()
    const { origin, destination } = urlParams

    if (origin && destination) {
      if (isEmpty(this.props.suggestionsData)) {
        this.getSuggestionsByIataCode(origin, destination, urlParams)
      } else {
        this.setOriginAndDestination()
      }
    }
  }

  setOriginAndDestination = () => {
    const { suggestionsTo, suggestionsFrom } = this.props.suggestionsData

    this.setState({
      values: {
        ...this.state.values,
        fromSelected: suggestionsFrom,
        toSelected: suggestionsTo
      }
    })
  }

  componentDidUpdate = prevProps => {
    if (isEmpty(prevProps.suggestionsData) && !isEmpty(this.props.suggestionsData)) {
      this.setOriginAndDestination()
    }
  }

  getSuggestionsByIataCode = (origin, destination, urlParams) => {
    if (isEmpty(this.props.suggestionsData) && origin && destination) {
      const route = `?iataCodes=${origin}&iataCodes=${destination}`
      const urlArray = [urlParams]

      urlArray[0].origin = origin.toUpperCase()
      urlArray[0].destination = destination.toUpperCase()

      this.props.fetchSuggestionsByIataCodes(urlArray, route)
    }
  }

  handleFromInputChange = (suggestionEvent, values, setFieldValue) => {
    if (suggestionEvent.reason === 'input-changed') {
      setFieldValue('fromSelected', values.fromSelected)
      this.searchSuggestion(suggestionEvent, 'suggestionsFrom')
    }
  }

  handleToInputChange = (suggestionEvent, values, setFieldValue) => {
    if (suggestionEvent.reason === 'input-changed') {
      setFieldValue('toSelected', values.toSelected)
      this.searchSuggestion(suggestionEvent, 'suggestionsTo')
    }
  }

  searchSuggestion = (suggestionEvent, name) => {
    if (suggestionEvent.value.length >= 3) {
      this.props.fetchSuggestions(suggestionEvent.value, 0, name)
    } else {
      this.props.clearSuggestions(name, 0)
    }
  }

  swapOriginDestination = (values, setFieldValue) => {
    const { fromSelected, toSelected } = values

    if (fromSelected && toSelected) {
      setFieldValue('fromSelected', toSelected)
      setFieldValue('toSelected', fromSelected)
    }
  }

  submitForm = values => {
    const { departureDate: departure, fromSelected, toSelected } = values

    const departureDate = getDateWithFormat(departure)
    const origin = fromSelected.cityCode.toUpperCase()
    const destination = toSelected.cityCode.toUpperCase()

    const searchURL = buildURLEncoded(generateFlightStatusRoute(this.props.i18n.language), {
      departureDate,
      origin,
      destination
    })

    this.props.setFlightStatusSearch(FLIGHT_STATUS.ROUTE)
    this.props.getFlightSchedule(departureDate, origin, destination, 'DAY')
    this.props.history.push(searchURL)
  }

  getSuggestions = (suggestions, selected) => {
    if (!suggestions || !suggestions[0]) {
      return []
    }

    return selected && selected.id
      ? suggestions[0].filter(({ id }) => id !== selected.id)
      : suggestions[0]
  }

  onAutoCompleteChange = (value, origin, setFieldValue) => {
    if (!value) {
      const field = origin ? 'fromSelected' : 'toSelected'

      setFieldValue(field, undefined)
    }
  }

  render = () => {
    const { toSuggestions, fromSuggestions, flightStatusPage } = this.props
    const showRightIcon =
      isMobile(this.props.mediaType) || (isTablet(this.props.mediaType) && !flightStatusPage)
    return (
      <Formik
        enableReinitialize
        initialValues={this.state.values}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={Yup.object().shape({
          fromSelected: Yup.object().required(
            this.props.t('general.flight-status.search.validation.origin.required')
          ),
          toSelected: Yup.object().required(
            this.props.t('general.flight-status.search.validation.destination.required')
          ),
          departureDate: Yup.string().required(
            this.props.t('general.flight-status.search.validation.flight-date.required')
          )
        })}
        onSubmit={() => {
          this.recaptchaRef.current?.execute()
        }}
      >
        {props => {
          const { values, errors, touched, setFieldValue } = props
          const isFlatStyle = !isEmpty(flightStatusPage)
          return (
            <Form>
              <FormWrapper flightStatusPage={flightStatusPage}>
                <AirportContainer>
                  <Label
                    htmlFor={this.origenInputId}
                    e2eClass={e2eClasses.statusFlightRouteOriginTitle}
                  >
                    {this.props.t('general.search-box.origin')}
                  </Label>
                  <Autocomplete
                    id={this.origenInputId}
                    className={this.origenInputId}
                    name="fromSelected"
                    placeholder={this.props.t('general.search-box.origin')}
                    onSuggestionsFetchRequested={event =>
                      this.handleFromInputChange(event, values, setFieldValue)
                    }
                    onSuggestionSelected={suggestion => setFieldValue('fromSelected', suggestion)}
                    value={values.fromSelected}
                    suggestions={this.getSuggestions(fromSuggestions, values.toSelected)}
                    shouldRenderSuggestions
                    error={touched.fromSelected && errors.fromSelected}
                    flatStyle={isFlatStyle}
                    onChange={value => this.onAutoCompleteChange(value, true, setFieldValue)}
                    rightButton={
                      showRightIcon && (
                        <SwapFieldsButton
                          onClick={() => this.swapOriginDestination(values, setFieldValue)}
                          rotateInTablet
                        />
                      )
                    }
                  />
                </AirportContainer>
                <SwapFieldsWrapper flightStatusPage={flightStatusPage}>
                  <SwapFieldsButton
                    onClick={() => this.swapOriginDestination(values, setFieldValue)}
                  />
                </SwapFieldsWrapper>
                <AirportContainer>
                  <Label
                    htmlFor={this.destinationInputId}
                    e2eClass={e2eClasses.statusFlightRouteDestinyTitle}
                  >
                    {this.props.t('general.search-box.destination')}
                  </Label>
                  <Autocomplete
                    id={this.destinationInputId}
                    placeholder={this.props.t('general.search-box.destination')}
                    onSuggestionsFetchRequested={event =>
                      this.handleToInputChange(event, values, setFieldValue)
                    }
                    onSuggestionSelected={suggestion => setFieldValue('toSelected', suggestion)}
                    value={values.toSelected}
                    suggestions={this.getSuggestions(toSuggestions, values.fromSelected)}
                    shouldRenderSuggestions
                    error={touched.toSelected && errors.toSelected}
                    name="toSelected"
                    flatStyle={isFlatStyle}
                    onChange={value => this.onAutoCompleteChange(value, false, setFieldValue)}
                  />
                </AirportContainer>
                <Separator />
                <InputWrapper>
                  <DatePickerFullResponsive
                    name="departureDate"
                    onChange={value => handleValueChange(null, props, 'departureDate', value)}
                    error={props.touched.departureDate && props.errors.departureDate}
                    format={FORMATS.DAY_PICKER_FORMAT}
                    placeholder={this.props.t('general.date-picker.date-format')}
                    value={props.values.departureDate}
                    flatStyle={isFlatStyle}
                    modalName={MODAL_NAMES.FLIGHT_ROUTE_DATE_PICKER_MOBILE}
                  />
                </InputWrapper>
                <Separator />
                <ButtonContainer>
                  <Button type="submit" large>
                    {this.props.t('general.flight-status.search.submit').toUpperCase()}
                  </Button>
                </ButtonContainer>
              </FormWrapper>
              <ReCaptcha
                ref={this.recaptchaRef}
                onSuccess={() => this.submitForm(values)}
                badge="bottomleft"
              />
            </Form>
          )
        }}
      </Formik>
    )
  }
}

const mapDispatchToProps = {
  clearSuggestions,
  fetchSuggestions,
  getFlightSchedule,
  fetchSuggestionsByIataCodes,
  setFlightStatusSearch
}

const mapStateToProps = state => ({
  toSuggestions: getSuggestionsTo(state),
  fromSuggestions: getSuggestionsFrom(state),
  suggestionsData: getSuggestionsFromTo(state),
  mediaType: mapMediaType(state)
})

export default withTranslation()(
  connect(mapStateToProps, mapDispatchToProps)(withRouter(FlightRoute))
)
